import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  // Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
  Button
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Favicon from 'components/Favicon';

const FeatureMatrix = () => {
  const theme = useTheme();

  // Get data from Redux
  const sbsData = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults?.side_by_side_analysis);

  // Access the analysis object
  const analysis = sbsData?.analysis || {};

  // Get feature matrix data
  const featureMatrixData = analysis['7_multi_dimensional_comparison_matrices']?.feature_matrix || [];

  // Get parameter weights for importance calculation
  // const parameterWeights = (analysis['4_weight_assignment'] || []).reduce((acc, param) => {
  //   acc[param.id] = param.weight_percent;
  //   return acc;
  // }, {});

  // Get list of companies from the first feature's scores object
  const companies = featureMatrixData.length > 0 && featureMatrixData[0].scores ? Object.keys(featureMatrixData[0].scores) : [];

  // Our company is always the first one (askpot.com)
  const ourCompany = companies[0] || 'My company';

  // State to track which companies are selected for display
  const [selectedCompanies, setSelectedCompanies] = useState(companies.reduce((acc, company) => ({ ...acc, [company]: true }), {}));

  // State to control notes visibility
  const [showNotes, setShowNotes] = useState(true);

  // Format feature names for display
  const formatFeatureName = (feature) => {
    return feature
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // // Get importance level for feature based on parameter weights
  // const getFeatureImportance = (featureId) => {
  //   // Get weight from parameter weights
  //   const weight = parameterWeights[featureId] || 0;
  //   // console.log(parameterWeights);
  //   // Convert weight percentage to importance level
  //   if (weight >= 15) return 3; // High (15-20%)
  //   if (weight >= 2) return 2; // Medium (7-14%)
  //   return 1; // Low (1-6%)
  // };

  // Handle checkbox change for company selection
  const handleCompanyToggle = (company) => {
    setSelectedCompanies({
      ...selectedCompanies,
      [company]: !selectedCompanies[company]
    });
  };

  // Toggle notes visibility
  const toggleNotes = () => {
    setShowNotes(!showNotes);
  };

  // Get a color for each company
  const getCompanyColor = (index, company) => {
    // Special highlight color for our company (Askpot)
    if (company === ourCompany) {
      return theme.palette.primary.main;
    }

    const colors = [
      '#71717A', // Gray for Kompyte
      '#22C55E', // Green for Crayon
      '#F59E0B', // Orange for Competitors App
      '#EF4444', // Red for osum.com
      '#06B6D4', // Cyan for competely.ai
      '#818CF8', // Lavender for panoramata.co
      theme.palette.info.main
    ];
    return colors[index % colors.length];
  };

  // Render status badge - now handling "YES"/"NO" string values
  const getStatusBadge = (status) => {
    if (status === 'YES' || status === 1 || status === true) {
      return (
        <CheckCircleOutlineIcon
          sx={{
            color: theme.palette.success.light,
            fontSize: 24
          }}
        />
      );
    } else {
      return (
        <HighlightOffIcon
          sx={{
            color: theme.palette.grey[400],
            fontSize: 24
          }}
        />
      );
    }
  };

  // Render importance badge (smaller version for inline use)
  // const getImportanceBadge = (importance, isSmall = false) => {
  //   const smallStyles = isSmall ? {
  //     height: 20,
  //     fontSize: '0.65rem',
  //     ml: 1
  //   } : {};

  //   switch (importance) {
  //     case 3: // High (previously 5 and 4)
  //       return (
  //         <Chip
  //           label="High"
  //           size="small"
  //           sx={{
  //             bgcolor: theme.palette.error.main,
  //             color: '#fff',
  //             ...smallStyles
  //           }}
  //         />
  //       );
  //     case 2: // Medium (previously 3)
  //       return (
  //         <Chip
  //           label="Medium"
  //           size="small"
  //           sx={{
  //             bgcolor: theme.palette.info.main,
  //             color: '#fff',
  //             ...smallStyles
  //           }}
  //         />
  //       );
  //     case 1: // Low (previously 2 and 1)
  //     default:
  //       return (
  //         <Chip
  //           label="Low"
  //           size="small"
  //           variant="outlined"
  //           sx={{
  //             ...smallStyles
  //           }}
  //         />
  //       );
  //   }
  // };

  // Handle empty or loading state
  if (!featureMatrixData || featureMatrixData.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              Feature Comparison Matrix
            </Typography>
            <Typography variant="body1">Loading data or no feature matrix data available.</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            Feature Comparison Matrix
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Detailed comparison of features across competing products
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Controls: Company Selection and Notes Toggle */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 3, justifyContent: 'space-between', alignItems: 'center' }}>
            <FormGroup row>
              {companies.map((company, index) => (
                <FormControlLabel
                  key={company}
                  control={
                    <Checkbox
                      checked={selectedCompanies[company]}
                      onChange={() => handleCompanyToggle(company)}
                      name={company}
                      sx={{
                        color: getCompanyColor(index, company),
                        '&.Mui-checked': {
                          color: getCompanyColor(index, company)
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Favicon company={company} size={16} />
                      {company === ourCompany ? (
                        <Box component="span" sx={{ fontWeight: 'bold', color: getCompanyColor(0, ourCompany) }}>
                          {company} (Our Company)
                        </Box>
                      ) : (
                        company
                      )}
                    </Box>
                  }
                />
              ))}
            </FormGroup>

            <Button
              variant="outlined"
              startIcon={showNotes ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={toggleNotes}
              size="small"
            >
              {showNotes ? 'Hide Notes' : 'Show Notes'}
            </Button>
          </Box>

          {/* Feature Matrix Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="medium">
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '220px' }}>Feature</TableCell>
                  {companies.map(
                    (company, index) =>
                      selectedCompanies[company] && (
                        <TableCell
                          key={company}
                          sx={{
                            fontWeight: 'bold',
                            color: getCompanyColor(index, company),
                            textTransform: 'uppercase',
                            maxWidth: '90px',
                            ...(company === ourCompany && {
                              backgroundColor: `${getCompanyColor(index, company)}14` // Light background with transparency
                            })
                          }}
                          align="center"
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Favicon company={company} size={20} />
                            {company === ourCompany ? (
                              <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {company}
                              </Box>
                            ) : (
                              company
                            )}
                          </Box>
                        </TableCell>
                      )
                  )}
                  {showNotes && (
                    <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' }, width: '40%' }}>
                      Evidence/Notes
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {featureMatrixData.map((feature) => {
                  // const importance = getFeatureImportance(feature.id);

                  return (
                    <TableRow key={feature.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>{formatFeatureName(feature.id)}</Box>
                      </TableCell>
                      {companies.map(
                        (company, index) =>
                          selectedCompanies[company] && (
                            <TableCell
                              key={`${feature.id}-${company}`}
                              align="center"
                              sx={{
                                ...(company === ourCompany && {
                                  backgroundColor: `${getCompanyColor(index, company)}14` // Light background with transparency
                                })
                              }}
                            >
                              {getStatusBadge(feature.scores?.[company])}
                            </TableCell>
                          )
                      )}
                      {showNotes && (
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.75rem', color: 'text.secondary' }}>
                          {feature._notes || 'No additional information available.'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Legend:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleOutlineIcon sx={{ color: theme.palette.success.light, fontSize: 16, mr: 0.5 }} />
              <Typography variant="caption">Feature available</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HighlightOffIcon sx={{ color: theme.palette.grey[400], fontSize: 16, mr: 0.5 }} />
              <Typography variant="caption">Feature not available</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeatureMatrix;
